import React, { Component } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import _isEqual from 'lodash/isEqual';

import { getTailwindColor } from '@trqk/utils';

interface Props<D> {
    chartDivId: string;
    data: D[];
    dataFields: DataFields;
    height: number;
    numberFormat?: string;
    onChartReady?(): void;
    tooltipLabels: TooltipLabels;
    zoomLevel: number;
}

const tertiary500 = am4core.color(getTailwindColor('tertiary-500'));

/**
 * Renders a horizontal bar chart appropriate for showing a limited amount of data, for example, the
 * top 5 tracks by detection count.
 */
export class SummaryBar<D> extends Component<Props<D>> {
    private chart: am4charts.XYChart | null = null;

    private categoryAxis: am4charts.CategoryAxis | null = null;

    componentDidMount(): void {
        const {
            data,
            dataFields,
            chartDivId,
            numberFormat,
            onChartReady,
            tooltipLabels,
            zoomLevel,
        } = this.props;
        const chart = am4core.create(chartDivId, am4charts.XYChart);
        const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
        const valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
        const series = chart.series.push(new am4charts.ColumnSeries());

        chart.data = data;
        if (numberFormat) {
            chart.numberFormatter.numberFormat = numberFormat;
        }
        chart.fontSize = 14;
        chart.padding(20, 0, 0, 0);

        categoryAxis.dataFields.category = dataFields.category;
        // Ensures that every category label is shown
        categoryAxis.renderer.minGridDistance = 0.01;
        categoryAxis.renderer.labels.template.fill = tertiary500;
        categoryAxis.renderer.labels.template.maxWidth = 300;
        categoryAxis.renderer.labels.template.fullWords = false;
        categoryAxis.renderer.labels.template.align = 'right';
        categoryAxis.renderer.labels.template.truncate = true;
        categoryAxis.renderer.grid.template.disabled = true;
        categoryAxis.align = 'right';

        // Value axis always starts at 0
        valueAxis.min = 0;
        // Precision for value axis labels is set to whole numbers i.e '0    1' rather than '0 0.5 1'
        valueAxis.maxPrecision = 0;
        valueAxis.renderer.labels.template.fill = tertiary500;
        valueAxis.renderer.maxLabelPosition = 0.95;
        // Min grid distance is set to ensure that labels show up for narrow widths
        valueAxis.renderer.minGridDistance = 50;

        series.columns.template.tooltipText = `[bold]${tooltipLabels.category}[/]: {categoryY}\n[bold]${tooltipLabels.value}[/]: {valueX}`;
        series.dataFields.valueX = dataFields.value;
        series.dataFields.categoryY = dataFields.category;
        series.name = 'Summary';

        categoryAxis.maxZoomCount = zoomLevel;
        categoryAxis.minZoomCount = zoomLevel;
        chart.zoomOutButton.disabled = true;

        this.categoryAxis = categoryAxis;
        this.chart = chart;
        this.zoomToIndexes = this.zoomToIndexes.bind(this);

        chart.events.on('ready', () => {
            if (onChartReady) {
                onChartReady();
            }

            this.zoomToIndexes();
        });
        chart.events.on('datavalidated', this.zoomToIndexes);
    }

    componentDidUpdate(prevProps: Props<D>): void {
        const { data } = this.props;

        if (!_isEqual(prevProps.data, data)) {
            if (this.chart !== null) {
                this.chart.data = data;
            }
        }
    }

    componentWillUnmount(): void {
        if (this.chart) {
            this.chart.dispose();
        }
    }

    zoomToIndexes(): void {
        const {
            categoryAxis,
            props: { zoomLevel },
        } = this;

        if (categoryAxis) {
            categoryAxis.zoomToIndexes(0, zoomLevel);
        }
    }

    render(): JSX.Element {
        const { chartDivId, height } = this.props;

        return (
            <div id={chartDivId} className="w-full pr-4" style={{ height }} />
        );
    }
}
